import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search, Vote, Users, BarChart3, UserCheck,
  Briefcase, GraduationCap, Heart, Calendar,
  Landmark, Receipt, CreditCard, TrendingUp,
  AlertTriangle, Building2, DollarSign, Trophy,
} from "lucide-react";
import KPICard from "../components/ui/KPICard";
import PoliticalMaps from "../components/PoliticalMaps";
import SEO from "../components/SEO";
import "../css/game-maps.css";

function resolvePhotoUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = (axios.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${url}`;
}

function capitalize(str) {
  if (!str) return "";
  return str.toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
}

function fmtBRL(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1e9) return `R$ ${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `R$ ${(n / 1e3).toFixed(0)}K`;
  return `R$ ${n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function ResultBadge({ situacao }) {
  if (!situacao) return null;
  const isEleito = situacao.includes("ELEITO");
  let cls = "bg-gray-100 text-gray-600";
  if (isEleito) cls = "bg-green-100 text-green-700";
  else if (situacao === "2º TURNO") cls = "bg-amber-100 text-amber-700";
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{capitalize(situacao)}</span>;
}

function StatsCard({ title, icon, children, className = "" }) {
  return (
    <div className={`bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        {icon && <span className="text-[var(--accent)]">{icon}</span>}
        <h2 className="text-lg font-bold text-[var(--text)]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SectionLabel({ icon, text }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      {icon && <span className="text-[var(--muted)]">{icon}</span>}
      <p className="text-xs text-[var(--muted)] uppercase tracking-wider">{text}</p>
    </div>
  );
}

const TABS = [
  { key: "total", label: "Todos", color: "var(--accent)" },
  { key: "eleitos", label: "Eleitos", color: "#16a34a" },
  { key: "naoEleitos", label: "Não Eleitos", color: "#dc2626" },
];

function val(row, tab) {
  if (tab === "total") return row.total;
  if (tab === "eleitos") return row.eleitos;
  return row.naoEleitos;
}

function HBar({ items, labelKey, tab, barColor, labelWidth = "w-36", showRank = false }) {
  const maxVal = Math.max(...items.map((r) => val(r, tab) || 0), 1);
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-BR"));

  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        const v = val(r, tab);
        if (!v) return null;
        return (
          <div key={r[labelKey]} className="flex items-center gap-2">
            {showRank && (
              <span className="text-[10px] font-bold text-[var(--muted)] w-4 text-right">{i + 1}</span>
            )}
            <span className={`text-xs text-[var(--text)] ${labelWidth} truncate`} title={capitalize(r[labelKey])}>
              {capitalize(r[labelKey])}
            </span>
            <div className="flex-1 h-3 bg-[var(--border)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${(v / maxVal) * 100}%`, backgroundColor: barColor, opacity: 0.65 }}
              />
            </div>
            <span className="text-xs text-[var(--muted)] w-16 text-right">{fmt(v)}</span>
          </div>
        );
      })}
    </div>
  );
}

function RankingList({ items, labelKey, valueKey, valueFmt, subtitleFn, color, icon }) {
  const maxVal = Math.max(...items.map((r) => r[valueKey] || 0), 1);
  return (
    <div className="space-y-2.5">
      {items.map((r, i) => {
        const v = r[valueKey] || 0;
        const pct = ((v / maxVal) * 100).toFixed(1);
        return (
          <div key={i} className="group">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-bold text-[var(--muted)] w-5 text-right">{i + 1}</span>
              {icon && <span className="text-[var(--muted)] opacity-60">{icon}</span>}
              <span className="text-xs font-medium text-[var(--text)] flex-1 truncate" title={capitalize(r[labelKey])}>
                {capitalize(r[labelKey])}
              </span>
              <span className="text-xs font-semibold" style={{ color }}>
                {valueFmt(v)}
              </span>
            </div>
            {subtitleFn && (
              <div className="ml-7 text-[10px] text-[var(--muted)]">{subtitleFn(r)}</div>
            )}
            <div className="ml-7 h-1.5 bg-[var(--border)] rounded-full overflow-hidden mt-0.5">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, backgroundColor: color, opacity: 0.5 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PoliticaPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resumo, setResumo] = useState(null);
  const [financeiro, setFinanceiro] = useState(null);
  const [tab, setTab] = useState("total");
  const debounceRef = useRef(null);
  const searchIdRef = useRef(0);

  useEffect(() => {
    axios.get("/politicos/resumo").then((res) => {
      if (res.data?.success) setResumo(res.data.data);
    }).catch(() => {});
    axios.get("/politicos/financeiro").then((res) => {
      if (res.data?.success) setFinanceiro(res.data.data);
    }).catch(() => {});
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 3) { setResults([]); setSearching(false); return; }
    const id = ++searchIdRef.current;
    setSearching(true);
    setResults([]);
    const seen = new Set();
    const enc = encodeURIComponent(q);

    for (const stage of [1, 2, 3]) {
      if (searchIdRef.current !== id) return;
      try {
        const res = await axios.get(`/politicos/busca?q=${enc}&stage=${stage}`);
        if (searchIdRef.current !== id) return;
        if (res.data?.success && res.data.data.length > 0) {
          const fresh = res.data.data.filter((c) => !seen.has(c.sqCandidato));
          fresh.forEach((c) => seen.add(c.sqCandidato));
          if (fresh.length > 0) setResults((prev) => [...prev, ...fresh]);
        }
      } catch {}
    }
    if (searchIdRef.current === id) setSearching(false);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 350);
  };

  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-BR"));

  const activeColor = TABS.find((t) => t.key === tab)?.color || "var(--accent)";

  return (
    <div className="max-w-5xl mx-auto">
      <SEO
        title="Política Brasileira - Candidatos e Eleições"
        description="Busque candidatos eleitos, partidos, prestação de contas de campanha e dados de eleições do Brasil, com fontes oficiais do TSE."
        path="/politica"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--text)] mb-2">Politica</h1>
        <p className="text-[var(--muted)]">Candidatos, eleicoes e dados politicos do Brasil</p>
      </div>

      {/* KPIs - Row 1: Candidatos */}
      {resumo && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <KPICard title="Candidatos" value={fmt(resumo.total)} icon={<Users size={20} />} />
          <KPICard title="Eleitos" value={fmt(resumo.totalEleitos)} icon={<UserCheck size={20} />} />
          <KPICard title="Partidos" value={fmt(resumo.totalPartidos)} icon={<BarChart3 size={20} />} />
          <KPICard
            title="% Mulheres"
            value={resumo.porGenero
              ? `${((resumo.porGenero.find((g) => g.genero === "FEMININO")?.total / resumo.total) * 100).toFixed(1)}%`
              : "—"}
            icon={<Vote size={20} />}
          />
        </div>
      )}

      {/* KPIs - Row 2: Financeiro */}
      {financeiro && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <KPICard
            title="Patrimonio Declarado"
            value={fmtBRL(financeiro.patrimonio?.total)}
            icon={<Landmark size={20} />}
          />
          <KPICard
            title="Receitas Campanha"
            value={fmtBRL(financeiro.receitas?.total)}
            icon={<TrendingUp size={20} />}
          />
          <KPICard
            title="Despesas Campanha"
            value={fmtBRL(financeiro.despesas?.total)}
            icon={<CreditCard size={20} />}
          />
          <KPICard
            title="Votos Nominais"
            value={fmt(financeiro.votos?.total)}
            icon={<Trophy size={20} />}
          />
        </div>
      )}

      {/* Search */}
      <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 mb-6">
        <h2 className="text-lg font-bold text-[var(--text)] mb-3">Buscar Politico Eleito</h2>
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            type="text"
            value={query}
            onChange={handleChange}
            placeholder="Buscar politico eleito (min. 3 caracteres)..."
            className="w-full pl-10 pr-4 py-3 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--text)] placeholder:text-[var(--muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-sm"
          />
        </div>
        {searching && results.length === 0 && (
          <div className="mt-4 text-center text-sm text-[var(--muted)]">Buscando...</div>
        )}
        {searching && results.length > 0 && (
          <div className="mt-2 text-center text-xs text-[var(--muted)] animate-pulse">carregando mais resultados...</div>
        )}
        {!searching && query.length >= 3 && results.length === 0 && (
          <div className="mt-4 text-center text-sm text-[var(--muted)]">Nenhum politico eleito encontrado para &quot;{query}&quot;</div>
        )}
        {results.length > 0 && (
          <div className="mt-4 space-y-1 max-h-[500px] overflow-y-auto">
            {results.map((c) => {
              const fotoUrl = resolvePhotoUrl(c.foto);
              return (
                <Link key={c.sqCandidato} to={`/politica/candidato/${c.sqCandidato}`}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[var(--border)]/20 transition-colors">
                  {fotoUrl ? (
                    <img src={fotoUrl} alt={c.nome} referrerPolicy="no-referrer"
                      className="w-10 h-10 rounded-full object-cover bg-[var(--border)]"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[var(--border)] flex items-center justify-center text-xs font-bold text-[var(--accent)]">
                      {(c.nome || "").split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--text)] truncate">{c.nome}</span>
                      <ResultBadge situacao={c.situacao} />
                    </div>
                    {c.nomeCompleto && c.nomeCompleto.toUpperCase() !== (c.nome || "").toUpperCase() && (
                      <div className="text-[11px] text-[var(--muted)] truncate italic">{capitalize(c.nomeCompleto)}</div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
                      <span className="font-semibold text-[var(--accent)]">{c.partido}</span>
                      <span>&middot;</span>
                      <span>{capitalize(c.cargo)}</span>
                      <span>&middot;</span>
                      <span>{capitalize(c.municipio)}, {c.uf}</span>
                      <span>&middot;</span>
                      <span>{c.ano}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Political Maps */}
      <PoliticalMaps />

      {/* ══════ Financial Section ══════ */}
      {financeiro && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            <DollarSign size={20} className="text-[var(--accent)]" />
            <h2 className="text-xl font-bold text-[var(--text)]">Prestacao de Contas 2024</h2>
            <span className="text-xs text-[var(--muted)] ml-2 bg-[var(--border)] px-2 py-0.5 rounded">
              {fmt(financeiro.receitas?.doacoes)} doacoes &middot; {fmt(financeiro.despesas?.lancamentos)} despesas
            </span>
          </div>

          {/* Row: Top Arrecadadores + Top Gastadores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Maiores Arrecadadores" icon={<TrendingUp size={16} />}>
              <RankingList
                items={financeiro.topArrecadadores || []}
                labelKey="nome" valueKey="total_receitas"
                valueFmt={fmtBRL}
                subtitleFn={(r) => `${r.partido} - ${capitalize(r.cargo)} (${r.uf})`}
                color="#16a34a"
              />
            </StatsCard>

            <StatsCard title="Maiores Gastadores" icon={<CreditCard size={16} />}>
              <RankingList
                items={financeiro.topGastadores || []}
                labelKey="nome" valueKey="total_despesas"
                valueFmt={fmtBRL}
                subtitleFn={(r) => `${r.partido} - ${capitalize(r.cargo)} (${r.uf})`}
                color="#dc2626"
              />
            </StatsCard>
          </div>

          {/* Row: Top Patrimonios + Top Votados */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Maiores Patrimonios" icon={<Landmark size={16} />}>
              <RankingList
                items={financeiro.topPatrimonios || []}
                labelKey="nome" valueKey="patrimonio"
                valueFmt={fmtBRL}
                subtitleFn={(r) => `${r.partido} - ${capitalize(r.cargo)} (${r.uf}) - ${r.qtd_bens} bens`}
                color="#8b5cf6"
              />
            </StatsCard>

            <StatsCard title="Mais Votados" icon={<Trophy size={16} />}>
              <RankingList
                items={financeiro.topVotados || []}
                labelKey="nome" valueKey="total_votos"
                valueFmt={(v) => fmt(v) + " votos"}
                subtitleFn={(r) => `${r.partido} - ${capitalize(r.cargo)} (${r.uf})`}
                color="#f59e0b"
              />
            </StatsCard>
          </div>

          {/* Row: Top Doadores + Top Fornecedores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Maiores Doadores" icon={<Receipt size={16} />}>
              <RankingList
                items={financeiro.topDoadores || []}
                labelKey="nome" valueKey="total"
                valueFmt={fmtBRL}
                subtitleFn={(r) => `${fmt(r.candidatos_financiados)} candidatos financiados`}
                color="#0ea5e9"
              />
            </StatsCard>

            <StatsCard title="Maiores Fornecedores" icon={<Building2 size={16} />}>
              <RankingList
                items={financeiro.topFornecedores || []}
                labelKey="nome" valueKey="total"
                valueFmt={fmtBRL}
                subtitleFn={(r) => `${fmt(r.candidatos_atendidos)} candidatos atendidos`}
                color="#f97316"
              />
            </StatsCard>
          </div>

          {/* Row: Concentracao Despesas + Cassacoes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Tipos de Despesa" icon={<CreditCard size={16} />}>
              {(() => {
                const items = financeiro.concentracaoDespesas || [];
                const maxV = Math.max(...items.map((r) => r.total || 0), 1);
                const totalAll = items.reduce((s, r) => s + (r.total || 0), 0) || 1;
                return (
                  <div className="space-y-2.5">
                    {items.map((r, i) => {
                      const pct = ((r.total / totalAll) * 100).toFixed(1);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs text-[var(--text)] truncate flex-1 mr-2" title={capitalize(r.tipo)}>
                              {capitalize(r.tipo)}
                            </span>
                            <span className="text-[10px] text-[var(--muted)] whitespace-nowrap">
                              {pct}% &middot; {fmtBRL(r.total)}
                            </span>
                          </div>
                          <div className="h-2 bg-[var(--border)] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${(r.total / maxV) * 100}%`, backgroundColor: "#ef4444", opacity: 0.45 }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </StatsCard>

            <StatsCard title="Motivos de Cassacao" icon={<AlertTriangle size={16} />}>
              {(() => {
                const items = financeiro.cassacoes || [];
                if (items.length === 0) return <p className="text-sm text-[var(--muted)]">Nenhuma cassacao registrada</p>;
                const maxV = Math.max(...items.map((r) => r.qty || 0), 1);
                return (
                  <div className="space-y-2.5">
                    {items.map((r, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-[var(--text)] truncate flex-1 mr-2" title={capitalize(r.motivo)}>
                            {capitalize(r.motivo)}
                          </span>
                          <span className="text-xs font-semibold text-red-500">{fmt(r.qty)}</span>
                        </div>
                        <div className="h-2 bg-[var(--border)] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${(r.qty / maxV) * 100}%`, backgroundColor: "#ef4444", opacity: 0.4 }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </StatsCard>
          </div>
        </div>
      )}

      {/* ══════ Profile Stats Section ══════ */}
      {resumo && (
        <>
          {/* Tab selector */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm font-medium text-[var(--muted)] mr-2">Perfil:</span>
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                style={tab === t.key
                  ? { backgroundColor: t.color, color: "#fff" }
                  : { backgroundColor: "var(--border)", color: "var(--muted)" }}
              >
                {t.label}
                <span className="ml-1.5 text-xs opacity-80">
                  {fmt(t.key === "total" ? resumo.total : t.key === "eleitos" ? resumo.totalEleitos : resumo.totalNaoEleitos)}
                </span>
              </button>
            ))}
          </div>

          {/* Row 1: Partidos + Genero/Raca */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Maiores Partidos" icon={<BarChart3 size={16} />}>
              <HBar items={(resumo.porPartido || []).slice(0, 12)} labelKey="partido"
                tab={tab} barColor={activeColor} labelWidth="w-14" />
            </StatsCard>

            <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 flex flex-col gap-5">
              {resumo.porGenero && (() => {
                const latestTotal = resumo.porGenero.reduce((s, g) => s + val(g, tab), 0) || 1;
                return (
                  <div>
                    <SectionLabel icon={<UserCheck size={14} />} text="Genero" />
                    <div className="flex h-5 rounded-full overflow-hidden">
                      {resumo.porGenero.map((g) => (
                        <div key={g.genero}
                          className="transition-all duration-500"
                          style={{
                            width: `${(val(g, tab) / latestTotal) * 100}%`,
                            backgroundColor: g.genero === "FEMININO" ? "#38bdf8" : activeColor,
                          }}
                          title={`${capitalize(g.genero)}: ${fmt(val(g, tab))}`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      {resumo.porGenero.map((g) => (
                        <span key={g.genero} className="text-xs text-[var(--muted)]">
                          {capitalize(g.genero)} — {((val(g, tab) / latestTotal) * 100).toFixed(1)}%
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {resumo.porRaca && (
                <div>
                  <SectionLabel icon={<Users size={14} />} text="Cor / Raca" />
                  <HBar items={resumo.porRaca.slice(0, 6)} labelKey="raca"
                    tab={tab} barColor={activeColor} labelWidth="w-28" />
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Escolaridade + Estado Civil */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Escolaridade" icon={<GraduationCap size={16} />}>
              <HBar items={resumo.porEscolaridade || []} labelKey="escolaridade"
                tab={tab} barColor="#10b981" labelWidth="w-44" />
            </StatsCard>

            <StatsCard title="Estado Civil" icon={<Heart size={16} />}>
              <HBar items={resumo.porEstadoCivil || []} labelKey="estadoCivil"
                tab={tab} barColor="#f43f5e" labelWidth="w-36" />
            </StatsCard>
          </div>

          {/* Row 3: Profissoes + Faixa Etaria */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <StatsCard title="Profissoes mais comuns" icon={<Briefcase size={16} />}>
              <HBar items={resumo.porOcupacao || []} labelKey="ocupacao"
                tab={tab} barColor="#f59e0b" labelWidth="w-44" showRank />
            </StatsCard>

            <StatsCard title="Faixa Etaria" icon={<Calendar size={16} />}>
              {resumo.porIdade && (() => {
                const maxV = Math.max(...resumo.porIdade.map((f) => val(f, tab) || 0), 1);
                const totalAge = resumo.porIdade.reduce((s, f) => s + (val(f, tab) || 0), 0) || 1;
                return (
                  <div className="space-y-3">
                    {resumo.porIdade.map((f) => {
                      const v = val(f, tab) || 0;
                      const pct = ((v / totalAge) * 100).toFixed(1);
                      return (
                        <div key={f.faixa}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-[var(--text)]">{f.faixa} anos</span>
                            <span className="text-xs text-[var(--muted)]">{fmt(v)} ({pct}%)</span>
                          </div>
                          <div className="h-4 bg-[var(--border)] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{ width: `${(v / maxV) * 100}%`, backgroundColor: "#8b5cf6", opacity: 0.6 }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </StatsCard>
          </div>
        </>
      )}
    </div>
  );
}
